/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import { parse as parseQuery } from 'query-string';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import Storage from '../lib/storage/Storage';

const SearchContext = React.createContext(null);

const searchStorage = new Storage();
const searchKey = 'FNC_PAST_SEARCHES';

export const useSearchContext = () => useContext(SearchContext);

export const useSearch = () => {
    const {
        value,
        setValue,
        recentSearches,
        addRecentSearch,
        setRecentSearches,
        removeRecentSearch,
        clearRecentSearches,
    } = useSearchContext();
    return {
        value,
        setValue,
        addRecentSearch,
        recentSearches,
        setRecentSearches,
        removeRecentSearch,
        clearRecentSearches,
    };
};

const propTypes = {
    value: PropTypes.string,
    children: PropTypes.node.isRequired,
};

const defaultProps = {
    value: null,
};

const getPastSearches = () => searchStorage.get(searchKey) || [];

const setLocalStorageSearches = (searches) => searchStorage.set(searchKey, searches);

export const SearchProvider = ({ value: initialValue, children }) => {
    const { search = null } = useLocation();
    const { search: searchValue = null } = parseQuery(search || {});
    const [value, setValue] = useState(initialValue || searchValue);
    const [recentSearches, setRecentSearches] = useState(getPastSearches());

    const removeRecentSearch = useCallback(
        (recentSearch) => {
            const newRecentSearches = recentSearches.filter((s) => s !== recentSearch);
            setRecentSearches(newRecentSearches);
            setLocalStorageSearches(newRecentSearches);
        },
        [recentSearches, setRecentSearches, setLocalStorageSearches],
    );

    const clearRecentSearches = useCallback(() => {
        setRecentSearches([]);
        setLocalStorageSearches([]);
    }, [setRecentSearches, setLocalStorageSearches]);

    const addRecentSearch = useCallback(
        (recentSearch) => {
            if (typeof recentSearch !== 'string') return;

            if (
                recentSearch !== '' &&
                recentSearches !== null &&
                !(recentSearches || []).includes(recentSearch)
            ) {
                const newValue = [recentSearch, ...recentSearches];
                const finalValue = newValue.length > 3 ? newValue.slice(0, 3) : newValue;
                setRecentSearches(finalValue);
                setLocalStorageSearches(finalValue);
            }
        },
        [recentSearches, setRecentSearches, setLocalStorageSearches],
    );

    const val = useMemo(
        () => ({
            value,
            setValue,
            recentSearches,
            addRecentSearch,
            removeRecentSearch,
            setRecentSearches,
            clearRecentSearches,
        }),
        [
            value,
            setValue,
            addRecentSearch,
            recentSearches,
            removeRecentSearch,
            setRecentSearches,
            clearRecentSearches,
        ],
    );
    return <SearchContext.Provider value={val}>{children}</SearchContext.Provider>;
};

SearchProvider.propTypes = propTypes;
SearchProvider.defaultProps = defaultProps;

export default SearchContext;
